import { Inject, Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { Observable, defer, of, from, throwError } from 'rxjs';
import MtbPlugin from './mtb.plugin';
import { catchError, map, switchMap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import Env from '../../env';
interface DeviceTokenResponse {
    afcCustomerId: number;
    accessToken: string;
    refreshToken: string;
}

@Injectable({
    providedIn: 'root'
})
export class DeviceIdService {
    readonly DEVICE_REFRESH_KEY: string = `${this.env.APP_NAME}-device-refresh`;
    readonly IS_USER_EXIST: string = `${this.env.APP_NAME}-user-exist`;

    constructor(
        @Inject('ENV') private env: Env,
        private httpClient: HttpClient
    ) {}

    getAccessDeviceToken() {
        return localStorage.getItem(`${this.env.APP_NAME}-device-access`);
    }

    isDeviceUserExist(): Observable<boolean> {
        if (Capacitor.isNativePlatform()) {
            return this.getDeviceId().pipe(
                switchMap((deviceId) => {
                    const url = `${this.env.WMB?.WMB_PUBLIC_API}/v1/device-customers/${deviceId}`;

                    return this.httpClient
                        .get<{
                            afcCustomerId: number;
                        }>(url)
                        .pipe(
                            map((response) => !!response.afcCustomerId),
                            catchError(() => of(false))
                        );
                })
            );
        } else {
            return of(!!this.getAccessDeviceToken());
        }
    }

    getDeviceCustomerId(): Observable<number | string | null> {
        if (Capacitor.isNativePlatform() && this.env.WMB?.WMB_PUBLIC_API) {
            return this.getDeviceId().pipe(
                switchMap((deviceId) => {
                    const url = `${this.env.WMB.WMB_PUBLIC_API}/v1/device-customers/${deviceId}`;

                    return this.httpClient
                        .get<{
                            afcCustomerId: string | number;
                        }>(url)
                        .pipe(
                            map((response) => response?.afcCustomerId ?? null),
                            catchError(() => of(null))
                        );
                })
            );
        }

        return of(null);
    }

    setLocalUserExist(isExist: boolean): Observable<any> {
        if (Capacitor.isNativePlatform()) {
            return from(
                Preferences.remove({ key: this.IS_USER_EXIST }).then(() => {
                    return Preferences.set({
                        key: this.IS_USER_EXIST,
                        value: JSON.stringify(isExist)
                    });
                })
            );
        }
        return of(null);
    }

    getIsUserExist(): Observable<string | boolean> {
        if (Capacitor.isNativePlatform()) {
            return from(Preferences.get({ key: this.IS_USER_EXIST })).pipe(map(({ value }) => JSON.parse(value)));
        }

        return of(true);
    }

    refreshAccessDeviceToken(): Observable<string> {
        if (!this.env.DEVICE_CUSTOMER_ENABLED || !Capacitor.isNativePlatform()) {
            return of(null);
        }

        return this.getRefreshDeviceIdToken().pipe(
            switchMap((token) => {
                if (token) {
                    return this.refreshDeviceIdTokenApi(token).pipe(
                        catchError((e) => {
                            // refresh token isn't correct. Create new token
                            if (e.status === 400 && e.error?.message === 'DEVICE_REFRESH_TOKEN_DOES_NOT_EXIST') {
                                return this.createDeviceIdToken();
                            }

                            throw e;
                        })
                    );
                }

                return this.createDeviceIdToken();
            }),
            switchMap(async (tokens: DeviceTokenResponse) => {
                localStorage.setItem(`${this.env.APP_NAME}-device-access`, tokens.accessToken);
                localStorage.setItem(`${this.env.APP_NAME}-device-refresh`, tokens.refreshToken);
                await Preferences.set({ key: `${this.env.APP_NAME}-device-access`, value: tokens.accessToken });
                await Preferences.set({ key: `${this.env.APP_NAME}-device-refresh`, value: tokens.refreshToken });

                return tokens.accessToken;
            })
        );
    }

    getDeviceId(): Observable<string> {
        return defer(() => {
            if (Capacitor.isNativePlatform()) {
                return MtbPlugin.deviceId();
            }
            return throwError(() => new Error('Device customer is only available in the native mobile app'));
        }).pipe(map((d) => d.deviceId));
    }

    private createDeviceIdToken(): Observable<DeviceTokenResponse> {
        return this.getDeviceId().pipe(
            switchMap((deviceId) => {
                return this.createDeviceIdTokenApi(deviceId);
            }),
            switchMap((tokens) => this.setLocalDeviceIdRefreshToken(tokens.refreshToken).pipe(map(() => tokens)))
        );
    }

    private getRefreshDeviceIdToken(): Observable<string> {
        if (Capacitor.isNativePlatform()) {
            return from(Preferences.get({ key: this.DEVICE_REFRESH_KEY })).pipe(map(({ value }) => value));
        }

        return of(localStorage.getItem(`${this.env.APP_NAME}-device-refresh`));
    }

    private setLocalDeviceIdRefreshToken(token: string): Observable<any> {
        if (Capacitor.isNativePlatform()) {
            return from(
                Preferences.remove({ key: this.DEVICE_REFRESH_KEY }).then(() => {
                    return Preferences.set({
                        key: this.DEVICE_REFRESH_KEY,
                        value: token
                    });
                })
            );
        }

        localStorage.setItem(`${this.env.APP_NAME}-device-refresh`, token);

        return of(null);
    }

    private refreshDeviceIdTokenApi(refreshToken: string): Observable<DeviceTokenResponse> {
        const url = `${this.env.WMB?.WMB_PUBLIC_API}/v1/device-customers/refresh-token`;
        const payload = {
            refreshToken: refreshToken
        };

        return this.httpClient.post<DeviceTokenResponse>(url, payload);
    }

    private createDeviceIdTokenApi(deviceId: string): Observable<DeviceTokenResponse> {
        const url = `${this.env.WMB?.WMB_PUBLIC_API}/v1/device-customers/${deviceId}`;

        return this.httpClient.post<DeviceTokenResponse>(url, {});
    }
}
